/**
 * Create and edit a unit.
 *
 * `marketRent` is kept as a STRING the whole way through — form state, payload,
 * display. It is never `Number()`d, because a float touching a rent figure is a bug
 * even when it renders correctly today (invariant 2). The API's zod schema refuses a
 * JSON number outright, so a stray `Number()` here would surface as a 422 rather
 * than silent drift, but the habit is the point.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from '@refinedev/core';
import { UNIT_STATUSES } from 'shared';
import {
  Field,
  FormErrors,
  Select,
  TextArea,
  TextInput,
  fieldErrors,
} from '../../../components/Form.js';

interface UnitRecord {
  id: string;
  propertyId: string;
  propertyName?: string;
  label: string;
  bedrooms: number;
  bathrooms: string;
  squareFeet: number | null;
  status: string;
  marketRent: string;
  notes: string | null;
}

interface UnitFormState {
  label: string;
  bedrooms: string;
  bathrooms: string;
  squareFeet: string;
  status: string;
  marketRent: string;
  notes: string;
}

const EMPTY: UnitFormState = {
  label: '',
  bedrooms: '1',
  bathrooms: '1.0',
  squareFeet: '',
  status: 'AVAILABLE',
  marketRent: '0.00',
  notes: '',
};

export function UnitForm() {
  // Either /staff/properties/:propertyId/units/new or /staff/units/:id/edit.
  const { id, propertyId } = useParams<{ id?: string; propertyId?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<UnitFormState>(EMPTY);

  const { onFinish, query, mutation } = useForm<UnitRecord>({
    resource: 'units',
    action: isEdit ? 'edit' : 'create',
    ...(isEdit ? { id } : {}),
    redirect: false,
  });

  const record = query?.data?.data;

  useEffect(() => {
    if (!record) return;
    setForm({
      label: record.label,
      bedrooms: String(record.bedrooms),
      bathrooms: record.bathrooms,
      squareFeet: record.squareFeet === null ? '' : String(record.squareFeet),
      status: record.status,
      marketRent: record.marketRent,
      notes: record.notes ?? '',
    });
  }, [record?.id, record]);

  const set =
    <K extends keyof UnitFormState>(key: K) =>
    (value: UnitFormState[K]) =>
      setForm((previous) => ({ ...previous, [key]: value }));

  const errors = fieldErrors(mutation.error);
  const parentPropertyId = propertyId ?? record?.propertyId;

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    const payload = {
      // Only on create — the API's update schema omits propertyId, because moving a
      // unit between properties would silently re-scope every lease on it.
      ...(isEdit ? {} : { propertyId: parentPropertyId }),
      label: form.label,
      bedrooms: Number(form.bedrooms),
      bathrooms: Number(form.bathrooms),
      squareFeet: form.squareFeet === '' ? null : Number(form.squareFeet),
      status: form.status,
      // A string, on purpose. See the header.
      marketRent: form.marketRent,
      notes: form.notes || null,
    };

    await onFinish(payload);
    if (parentPropertyId) void navigate(`/staff/properties/${parentPropertyId}`);
  }

  if (isEdit && query?.isLoading) return <p className="muted">Loading…</p>;

  return (
    <section>
      <h1>
        {isEdit ? `Edit unit ${record?.label ?? ''}` : 'Add unit'}
        {record?.propertyName ? <span className="muted"> · {record.propertyName}</span> : null}
      </h1>

      <form className="stack card" onSubmit={(event) => void submit(event)}>
        <FormErrors error={mutation.error} />

        <Field
          label="Label"
          name="label"
          required
          hint="“2B”, “Upper”, “1”. Unique within the property."
          error={errors.label}
        >
          <TextInput name="label" value={form.label} onChange={set('label')} required />
        </Field>

        <Field label="Bedrooms" name="bedrooms" required error={errors.bedrooms}>
          <TextInput
            name="bedrooms"
            type="number"
            min="0"
            max="20"
            value={form.bedrooms}
            onChange={set('bedrooms')}
            required
          />
        </Field>

        <Field
          label="Bathrooms"
          name="bathrooms"
          required
          hint="Halves allowed — 1.5 is common."
          error={errors.bathrooms}
        >
          <TextInput
            name="bathrooms"
            type="number"
            min="0"
            max="20"
            step="0.5"
            value={form.bathrooms}
            onChange={set('bathrooms')}
            required
          />
        </Field>

        <Field label="Square feet" name="squareFeet" error={errors.squareFeet}>
          <TextInput
            name="squareFeet"
            type="number"
            min="0"
            value={form.squareFeet}
            onChange={set('squareFeet')}
          />
        </Field>

        <Field label="Status" name="status" required error={errors.status}>
          <Select
            name="status"
            value={form.status}
            onChange={set('status')}
            options={UNIT_STATUSES}
          />
        </Field>

        <Field
          label="Market rent"
          name="marketRent"
          required
          hint="Asking rent. The contractual amount lives on the lease."
          error={errors.marketRent}
        >
          <TextInput
            name="marketRent"
            // `text`, not `number`. A number input hands back a value the browser has
            // already coerced through a float, and for money the decimal string is
            // the value we want to send verbatim.
            type="text"
            value={form.marketRent}
            onChange={set('marketRent')}
            required
          />
        </Field>

        <Field label="Notes" name="notes" error={errors.notes}>
          <TextArea name="notes" value={form.notes} onChange={set('notes')} />
        </Field>

        <div className="row">
          <button type="submit" className="button" disabled={mutation.isLoading}>
            {mutation.isLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Create unit'}
          </button>
          <button
            type="button"
            className="button button--quiet"
            onClick={() =>
              void navigate(
                parentPropertyId ? `/staff/properties/${parentPropertyId}` : '/staff/properties',
              )
            }
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
